import {
  Button,
  DatePicker,
  DateRange,
  IconWarningAlt,
  MomentObj,
  Select,
  Table,
  TextBox,
} from "@veneer/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import "../../assets/css/views/reports/reports.css";
import moment from "moment";
import LoaderModal from "../../components/Loader/loaderModal";
import ReportsLogic from "./ReportsLogic";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

// Define the type for each item in the select options
interface ISelectOption {
  value: number;
  label: string;
}

// Define the type for the date range
export interface IDateRange {
  startDate: string | moment.Moment | undefined;
  endDate: string | moment.Moment | undefined;
}

interface IDataReport {
  sp: string;
  value: string;
}

/**
 * Reports view component.
 */
export default function Reports() {
  const COLUMNS_INITIAL_VISIBLE = 12;

  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [value, setValue] = useState<number[]>([]);
  const [showAllColumns, setShowAllColumns] = useState<boolean>(false);
  const [isColumnsSorted, setIsColumnsSorted] = useState<boolean>(false);
  const [reportSelected, setReportSelected] = useState<IDataReport>({
    value: "",
    sp: "",
  });
  const [isWeekDate, setIsWeekDate] = useState<boolean>(false);
  const [isMonthDate, setIsMonthDate] = useState<boolean>(false);
  const [selectedYearMonthlyReport, setSelectedYearMonthlyReport] =
    useState<ISelectOption>();
  const [selectedMonthMonthlyReport, setSelectedMonthMonthlyReport] =
    useState<ISelectOption>();
  const [optionsYears, setOptionsYears] = useState<ISelectOption[]>();
  const [optionsMonths, setOptionsMonths] = useState<ISelectOption[]>();
  const [dateRange, setDateRange] = useState<IDateRange>({
    startDate: undefined,
    endDate: undefined,
  });
  const [t] = useTranslation("global");
  const {
    getReportList,
    loadColumns,
    dataReportList,
    dataReportListFiltered,
    loading,
    columnsLoading,
    exportLoading,
    reportmessage,
    handleChangeSearchReport,
    calculateMonthsAvailablesToSelect,
    calculateYearsAvailablesToSelect,
    // Phase
    phase,
    resetToInitial,
    goBackToColumns,
    // Column selection
    columnSelectOptions,
    selectedColumnIndices,
    handleToggleColumn,
    handleClearColumns,
    handleSelectAllColumns,
    generatePreview,
    // Preview
    selectedColumns,
    columnFilters,
    globalSearch,
    handleGlobalSearchChange,
    clearAllFilters,
    pagedData,
    tableColumns,
    tablePreferences,
    tablePagination,
    filteredPreviewData,
    handleExportExcel,
    handleExportFilteredExcel,
  } = ReportsLogic();

  // Track previous phase to determine if dates should reset
  const [prevPhase, setPrevPhase] = useState<string>("initial");

  useEffect(() => {
    getReportList();
  }, [languageID]);

  /**
   * Handles the change event of the select component.
   * @param option - The selected option.
   */
  useEffect(() => {
    if (phase === "columns") {
      setShowAllColumns(false);
      setIsColumnsSorted(false);
      // Only reset dates when coming from initial (new report selected), not from preview (going back)
      if (prevPhase === "initial") {
        setDateRange({ startDate: undefined, endDate: undefined });
        setSelectedYearMonthlyReport(undefined);
        setSelectedMonthMonthlyReport(undefined);
      }
    }
    setPrevPhase(phase);
  }, [phase]);

  const visibleColumnOptions = useMemo(() => {
    if (!isColumnsSorted) return columnSelectOptions;
    return [...columnSelectOptions].sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [columnSelectOptions, isColumnsSorted]);

  const onChange = (option: any) => {
    setValue([option.value]);
    const selected = { value: option?.label, sp: option?.sp };
    setReportSelected(selected);
    // Reset dates when selecting a new report
    setDateRange({ startDate: undefined, endDate: undefined });
    setSelectedYearMonthlyReport(undefined);
    setSelectedMonthMonthlyReport(undefined);
    // Immediately fetch column metadata — no Search button needed
    loadColumns(option?.sp, option?.label);
  };

  /**
   * Clears the selected value of the select component.
   */
  const onClear = () => {
    setValue([]);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
    setIsWeekDate(false);
    setIsMonthDate(false);
    setSelectedMonthMonthlyReport(undefined);
    setSelectedYearMonthlyReport(undefined);
    setReportSelected({ value: "", sp: "" });
  };

  /**
   * Handles the date change event of the date picker component.
   * @param key - The key of the date range property to update.
   * @param value - The new value of the date range property.
   */

  const handleOnDateChange = (
    key: keyof IDateRange,
    value: MomentObj | DateRange | null | undefined
  ) => {
    setDateRange((prev) => {
      let newValue: any;
      if (value && "isBefore" in value) {
        newValue = value;
      } else if (value && "startDate" in value && "endDate" in value) {
        newValue = key === "endDate" ? value : value;
      } else {
        newValue = undefined;
      }

      if (key === "startDate") {
        return {
          startDate: newValue,
          endDate: undefined,
        };
      }

      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const isStartDateOutsideRangeEditMass = (day: MomentObj) => {
    if (reportSelected.sp === "GetSheduledAgendasReport") {
      return false;
    }

    const endDate = moment.isMoment(dateRange.endDate)
      ? dateRange.endDate
      : moment(dateRange.endDate);

    if (!endDate.isValid()) {
      return day.isBefore(moment(), "day");
    }
    return day.isAfter(endDate, "day") || day.isAfter(moment(), "day");
  };

  const isEndDateOutsideRangeEditMass = (day: MomentObj) => {
    if (reportSelected.sp === "GetSheduledAgendasReport") {
      return false;
    }

    const startDate = moment.isMoment(dateRange.startDate)
      ? dateRange.startDate
      : moment(dateRange.startDate);

    if (!startDate.isValid()) {
      return day.isBefore(moment(), "day");
    }
    const endDateLimitBefore = startDate.clone().add(3, "months");
    const validationEndDateLimitBefore =
      day.isBefore(startDate, "day") || day.isAfter(endDateLimitBefore, "day");
    return validationEndDateLimitBefore || day.isAfter(moment(), "day");
  };

  const isDisabledButtonSearch = useCallback(() => {
    if (
      (reportSelected?.sp !== "" && dateRange.endDate && dateRange.startDate) ||
      isReportWithoutDates(reportSelected) ||
      (isWeekDate && dateRange.startDate) ||
      (isMonthDate && dateRange.startDate)
    ) {
      return false;
    } else {
      return true;
    }
  }, [
    reportSelected,
    dateRange.startDate,
    dateRange.endDate,
    isWeekDate,
    isMonthDate,
  ]);

  const isDisabledDates = useCallback(() => {
    return reportSelected?.sp === "" || isReportWithoutDates(reportSelected);
  }, [reportSelected]);

  const isReportWithoutDates = (report: IDataReport) => {
    return (
      report.sp === "GetPointsOfSaleReport" || report.sp === "GetUsersReport"
    );
  };

  const isWeekDateOutsideRange = (day: MomentObj | undefined) => {
    day = day ?? moment();
    const currentDate = moment();
    const startOfCurrentWeek = currentDate.startOf("week");
    const isBeforeCurrentWeek = day.isBefore(startOfCurrentWeek);
    const mondayIndex = 1;
    const mondayOfTheWeek = day.clone().weekday(mondayIndex);

    return !day.isSame(mondayOfTheWeek) || !isBeforeCurrentWeek;
  };

  useEffect(() => {
    setIsWeekDate(reportSelected.sp === "GetWeeklyCapturesReport");
  }, [reportSelected]);

  useEffect(() => {
    setIsMonthDate(reportSelected.sp === "GetMonthlyCapturesReport");
  }, [reportSelected]);

  useEffect(() => {
    setOptionsYears(calculateYearsAvailablesToSelect());
  }, [calculateYearsAvailablesToSelect]);

  useEffect(() => {
    setOptionsMonths(
      calculateMonthsAvailablesToSelect(selectedYearMonthlyReport?.value)
    );
  }, [selectedYearMonthlyReport, calculateMonthsAvailablesToSelect]);

  useEffect(() => {
    setSelectedMonthMonthlyReport(undefined);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
  }, [selectedYearMonthlyReport]);

  return (
    <div className="reports">
      {/* ── Loader ───────────────────────────────────────────────────────────── */}
      {(loading || columnsLoading || exportLoading) && (
        <LoaderModal>
          <div>
            {loading
              ? `${t("header.generating")} ${reportSelected.value}`
              : columnsLoading
              ? t("header.loading")
              : t("header.exportExcel")}
          </div>
        </LoaderModal>
      )}

      {phase !== "preview" && (
        <h4 className="title-header">{t("header.reports")}</h4>
      )}

      {/* ── Step 1: Report Selector only (no dates, no Search button) ────────── */}
      {phase === "initial" && (
        <div className="report-header">
          <div className="form-item">
            <Select
              options={dataReportListFiltered}
              id="select-usage"
              label={t("modal.SelectOption")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={onChange}
              onClear={onClear}
              value={value}
              disabled={dataReportList.length === 0}
              showSearch={true}
              onSearch={handleChangeSearchReport}
            />
          </div>
        </div>
      )}

      {/* ── Alerts ───────────────────────────────────────────────────────────── */}
      {dataReportList.length === 0 && !loading && !columnsLoading && phase === "initial" && (
        <div className="alert-container">
          <div>
            <IconWarningAlt style={{ width: "80px", height: "80px" }} />
          </div>
          <p>{t("header.noItemsFound")}</p>
        </div>
      )}
      {reportmessage !== "" && !loading && !(phase === "columns" && isWeekDate) && (
        <div className="alert-container">
          <div>
            <IconWarningAlt
              style={{
                width: "80px",
                height: "80px",
                color:
                  reportmessage === "donwload_failed" ? "#D9534F" : "#0096D6",
              }}
            />
          </div>
          <p>{t(`header.${reportmessage}`) + " " + reportSelected.value}</p>
        </div>
      )}

      {/* Step 2: Column Selection — includes date pickers + column cards */}
      {phase === "columns" && (
        <div className="report-columns-section">
          {/* Breadcrumb navigation */}
          <nav className="report-preview-nav report-columns-nav">
            <button
              type="button"
              className="report-nav-link"
              onClick={resetToInitial}
            >
              {t("header.reports")}
            </button>
            <span className="report-nav-sep">›</span>
            <span className="report-nav-active">{reportSelected.value}</span>
          </nav>
          <div className="report-columns-box">

            {/* ── Date pickers live here in columns phase ───────────────────── */}
            {!isReportWithoutDates(reportSelected) && (
              <div className="report-header" style={{ marginBottom: 0 }}>
                {!isWeekDate && !isMonthDate && (
                  <>
                    <div className="form-item">
                      <DatePicker
                        calendarOrientation={["right", "down"]}
                        label={t("header.stardDate")}
                        onChange={(date) => handleOnDateChange("startDate", date)}
                        orientation="horizontal"
                        placeholder="MM/DD/YYYY"
                        value={dateRange.startDate ? dateRange.startDate.toString() : undefined}
                        isOutsideRange={isStartDateOutsideRangeEditMass}
                      />
                    </div>
                    <div className="form-item">
                      <DatePicker
                        calendarOrientation={["right", "down"]}
                        label={t("header.endDate")}
                        onChange={(date) => handleOnDateChange("endDate", date)}
                        orientation="horizontal"
                        placeholder="MM/DD/YYYY"
                        value={dateRange.endDate ? dateRange.endDate.toString() : undefined}
                        isOutsideRange={isEndDateOutsideRangeEditMass}
                      />
                    </div>
                  </>
                )}
                {isWeekDate && (
                  <div className="form-item">
                    <DatePicker
                      calendarOrientation={["right", "down"]}
                      label={t("header.date")}
                      onChange={(date) =>
                        (!isWeekDateOutsideRange(date as MomentObj) || !date) &&
                        handleOnDateChange("startDate", date)
                      }
                      orientation="horizontal"
                      placeholder="MM/DD/YYYY"
                      value={dateRange.startDate ? dateRange.startDate.toString() : undefined}
                      enableOutsideDays
                      monthYearNavigation
                      isOutsideRange={isWeekDateOutsideRange}
                      maxDate={moment().weekday(-7)}
                    />
                  </div>
                )}
                {isMonthDate && (
                  <>
                    <div className="form-item">
                      <Select
                        options={optionsYears ?? []}
                        id="select-year-monthly-report"
                        label={t("header.year")}
                        placement="bottom"
                        helperTextVisibility="auto"
                        onChange={(option: any) =>
                          setSelectedYearMonthlyReport({ label: option?.label, value: option?.value })
                        }
                        clearIcon={false}
                        value={selectedYearMonthlyReport && [selectedYearMonthlyReport.value]}
                      />
                    </div>
                    <div className="form-item">
                      <Select
                        options={optionsMonths ?? []}
                        id="select-month-monthly-report"
                        label={t("header.month")}
                        placement="bottom"
                        helperTextVisibility="auto"
                        onChange={(option: any) => {
                          option && setSelectedMonthMonthlyReport({ label: option?.label, value: option?.value });
                          option && handleOnDateChange("startDate", moment(new Date(selectedYearMonthlyReport?.value ?? 2024, option.value)));
                        }}
                        clearIcon={false}
                        value={selectedMonthMonthlyReport ? [selectedMonthMonthlyReport.value] : []}
                        disabled={!selectedYearMonthlyReport || selectedYearMonthlyReport.value === 0}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            {!isWeekDate && (
              <>
                {/* ── Column selection cards ────────────────────────────────────── */}
                <div className="report-columns-title-row">
                  <h5 className="report-section-title">
                    {t("header.availableColumns")}
                  </h5>
                  <div className="report-columns-actions">
                    <button
                      type="button"
                      className="report-select-all-btn"
                      onClick={() => setIsColumnsSorted((prev) => !prev)}
                    >
                      {isColumnsSorted ? t("header.sortZA") : t("header.sortAZ")}
                    </button>
                    <button
                      type="button"
                      className="report-select-all-btn"
                      onClick={() =>
                        selectedColumnIndices.length === columnSelectOptions.length
                          ? handleClearColumns()
                          : handleSelectAllColumns()
                      }
                    >
                      {selectedColumnIndices.length === columnSelectOptions.length
                        ? t("header.deselectAll")
                        : t("header.selectAll")}
                    </button>
                  </div>
                </div>
                <div className="report-column-grid">
                  {(
                    showAllColumns
                      ? visibleColumnOptions
                      : visibleColumnOptions.slice(0, COLUMNS_INITIAL_VISIBLE)
                  ).map((opt: { value: number; label: string }) => {
                    const isSelected = selectedColumnIndices.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`report-column-card${
                          isSelected ? " selected" : ""
                        }`}
                        aria-pressed={isSelected}
                        onClick={() => handleToggleColumn(opt.value)}
                      >
                        <span className="report-column-card-check" />
                        <span className="report-column-card-label">
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                  {columnSelectOptions.length > COLUMNS_INITIAL_VISIBLE && (
                    <button
                      type="button"
                      className="report-column-card report-column-more"
                      onClick={() => setShowAllColumns((v) => !v)}
                    >
                      {showAllColumns
                        ? `${t("header.showLess")} ▲`
                        : `${t("header.showMore")} ▼`}
                    </button>
                  )}
                </div>
                <span className="report-columns-count">
                  {selectedColumnIndices.length} {t("header.of")}{" "}
                  {columnSelectOptions.length} {t("header.columnsSelected")}
                </span>
              </>
            )}
          </div>
          <div className="report-columns-footer">
            <Button
              onClick={() => generatePreview(reportSelected, dateRange)}
              disabled={
                (!isWeekDate && selectedColumnIndices.length === 0) ||
                isDisabledButtonSearch()
              }
            >
              {isWeekDate ? t("header.exportExcel") : t("header.generatePreview")}
            </Button>
          </div>
          
          {/* Alert for Weekly Captures Report - shown below the form */}
          {isWeekDate && reportmessage !== "" && !loading && (
            <div className="alert-container">
              <div>
                <IconWarningAlt
                  style={{
                    width: "80px",
                    height: "80px",
                    color:
                      reportmessage === "donwload_failed" ? "#D9534F" : "#0096D6",
                  }}
                />
              </div>
              <p>{t(`header.${reportmessage}`) + " " + reportSelected.value}</p>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Preview Table */}
      {phase === "preview" && (
        <div className="report-preview-section">
          {/* Breadcrumb navigation */}
          <nav className="report-preview-nav">
            <button
              type="button"
              className="report-nav-link"
              onClick={resetToInitial}
            >
              {t("header.reports")}
            </button>
            <span className="report-nav-sep">›</span>
            <button
              type="button"
              className="report-nav-link"
              onClick={goBackToColumns}
            >
              {reportSelected.value}
            </button>
            <span className="report-nav-sep">›</span>
            <span className="report-nav-active">
              {t("header.reportPreview")}
            </span>
          </nav>
          {/* Header: title + export */}
          <div className="report-preview-header">
            <h5 className="report-section-title">
              {t("header.reportPreview")} ({t("header.showing")}{" "}
              {pagedData.length} {t("header.of")} {filteredPreviewData.length}{" "}
              {t("header.records")})
            </h5>
            <div className="report-preview-actions">
              <Button
                onClick={handleExportFilteredExcel}
                disabled={filteredPreviewData.length === 0 || exportLoading}
              >
                {t("header.exportFilteredColumns")}
              </Button>
              <Button
                onClick={handleExportExcel}
                disabled={selectedColumns.length === 0 || exportLoading}
              >
                {t("header.exportExcel")}
              </Button>
            </div>
          </div>

          {/* Slim toolbar: global search + clear all filters */}
          <div className="report-table-toolbar">
            <TextBox
              id="global-search"
              label={t("header.searchInTable")}
              type="search"
              value={globalSearch}
              onChange={(val: string) => handleGlobalSearchChange(val)}
            />
            {(Object.keys(columnFilters).length > 0 || globalSearch) && (
              <Button appearance="ghost" onClick={clearAllFilters}>
                {t("header.clearFilters")}
              </Button>
            )}
          </div>

          {/* Preview Table — column-header filters rendered natively by Veneer */}
          {pagedData.length > 0 ? (
            <div className="report-table-container">
              <Table
                columns={tableColumns}
                data={pagedData}
                pagination={tablePagination}
                preferences={tablePreferences}
                showFilters={true}
              />
            </div>
          ) : (
            <div className="alert-container">
              <div>
                <IconWarningAlt style={{ width: "80px", height: "80px" }} />
              </div>
              <p>{t("header.noItemsFound")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
